import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Cloud, Database, HardDrive, Globe, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

// Match-kind → Icon + kurzes Label (Deutsch, Ops-Kontext)
const KIND = {
    cloud: { icon: Cloud, label: "Cloud" },
    domain: { icon: Globe, label: "Domain" },
    mysql: { icon: Database, label: "MySQL" },
    "mysql-mail": { icon: Database, label: "Mail DB" },
    s3: { icon: HardDrive, label: "S3" },
    "s3-mail": { icon: HardDrive, label: "Mail S3" },
};

const FIELD_LABEL = {
    cloud_id: "Cloud ID",
    name: "Name",
    primary_domain: "Domain",
    fallback_hostname: "Fallback",
    domain: "Domain",
    db_user: "DB user",
    db_name: "DB name",
    db_host: "DB host",
    s3_bucket: "Bucket",
    s3_access_key: "Access key",
};

const statusColor = (s) =>
    s === "active" ? "bg-emerald-500"
        : s === "suspended" ? "bg-zinc-500"
            : s === "error" ? "bg-red-500"
                : "bg-amber-500";

export const GlobalSearch = () => {
    const nav = useNavigate();
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const boxRef = useRef(null);

    // Debounced Server-Suche
    useEffect(() => {
        const term = q.trim();
        if (term.length < 2) {
            setData(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const r = await api.get("/api/admin/search", { params: { q: term } });
                setData(r.data);
            } catch {
                setData({ results: [], count: 0 });
            } finally {
                setLoading(false);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [q]);

    // Klick außerhalb schließt das Dropdown
    useEffect(() => {
        const onDoc = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const results = data?.results || [];

    const goTo = (cloudId) => {
        setOpen(false);
        setQ("");
        setData(null);
        nav(`/clouds/${cloudId}`);
    };

    const onKeyDown = (e) => {
        if (e.key === "Escape") {
            setOpen(false);
            e.currentTarget.blur();
        } else if (e.key === "Enter") {
            // Bei genau EINEM Treffer direkt springen, sonst Liste offen lassen.
            const existing = results.filter((r) => r.cloud_exists);
            if (existing.length === 1) goTo(existing[0].cloud_id);
        }
    };

    return (
        <div className="relative" ref={boxRef} data-testid="global-search">
            <Search size={13} className="absolute left-2.5 top-2.5 text-zinc-500" strokeWidth={1.5} />
            <input
                data-testid="global-search-input"
                className="cm-input pl-8 pr-7 w-full text-[13px]"
                value={q}
                onFocus={() => setOpen(true)}
                onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                onKeyDown={onKeyDown}
                placeholder="Search: cloud, domain, MySQL user, S3…"
            />
            {q && (
                <button
                    data-testid="global-search-clear"
                    onClick={() => { setQ(""); setData(null); }}
                    className="absolute right-2 top-2 text-zinc-500 hover:text-zinc-200"
                    aria-label="Clear search"
                >
                    <X size={14} />
                </button>
            )}

            {open && q.trim().length >= 2 && (
                <div
                    data-testid="global-search-results"
                    className="absolute left-0 top-full mt-1 w-80 max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto bg-[#0B0E14] border border-zinc-800 shadow-2xl z-[60] rounded-sm"
                >
                    {loading && (
                        <div className="flex items-center gap-2 px-3 py-3 text-xs text-zinc-400">
                            <Loader2 size={14} className="animate-spin" /> Searching…
                        </div>
                    )}
                    {!loading && results.length === 0 && (
                        <div data-testid="global-search-empty" className="px-3 py-3 text-xs text-zinc-500">
                            No match for &quot;{q.trim()}&quot;
                        </div>
                    )}
                    {!loading && results.length > 0 && (
                        <div className="py-1">
                            <div className="px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-zinc-500">
                                {data.count} matches
                            </div>
                            {results.map((r) => (
                                <button
                                    key={r.cloud_id}
                                    data-testid={`global-search-result-${r.cloud_id}`}
                                    disabled={!r.cloud_exists}
                                    onClick={() => r.cloud_exists && goTo(r.cloud_id)}
                                    className="w-full text-left px-3 py-2 hover:bg-zinc-900/70 disabled:opacity-50 disabled:cursor-not-allowed border-b border-zinc-900 last:border-b-0"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusColor(r.status)}`} />
                                        <span className="text-[13px] text-zinc-100 font-medium truncate">
                                            {r.cloud_name || r.primary_domain || r.cloud_id}
                                        </span>
                                        {!r.cloud_exists && (
                                            <span className="text-[10px] font-mono text-red-400 uppercase">orphaned</span>
                                        )}
                                    </div>
                                    {r.primary_domain && (
                                        <div className="text-xs text-zinc-500 truncate ml-3.5">{r.primary_domain}</div>
                                    )}
                                    <div className="flex flex-wrap gap-1 mt-1 ml-3.5">
                                        {r.matches.slice(0, 4).map((m, i) => {
                                            const K = KIND[m.kind] || KIND.cloud;
                                            const Icon = K.icon;
                                            return (
                                                <span
                                                    key={i}
                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-zinc-900 border border-zinc-800 rounded-sm text-[11px] text-zinc-400"
                                                    title={`${FIELD_LABEL[m.field] || m.field}: ${m.value}`}
                                                >
                                                    <Icon size={10} strokeWidth={1.5} className="text-zinc-500" />
                                                    <span className="font-mono truncate max-w-[140px]">{m.value}</span>
                                                </span>
                                            );
                                        })}
                                        {r.matches.length > 4 && (
                                            <span className="text-[11px] text-zinc-600">+{r.matches.length - 4}</span>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
